import { useEffect, useState } from "react";
import { formatBuiltAtUtc, type ReleaseBuildInfo, type ReleaseRegistryEntry } from "@structura/core/release-metadata";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ReleaseEnvironmentBadge } from "@/components/platform/ReleaseEnvironmentBadge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { PageLoader } from "@/components/PageLoader";
import { cn } from "@/lib/utils";
import type { ReactNode } from "react";

type Payload = {
  build: ReleaseBuildInfo;
  pointers: { prod: string | null; latestRc: string | null; devBranch?: string };
  registryUpdatedAt: string;
  history: ReleaseRegistryEntry[];
};

export function AdminReleasesPanel() {
  const [data, setData] = useState<Payload | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch("/api/release-info")
      .then((r) => r.json())
      .then((d: Payload) => setData(d))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return <PageLoader label="Cargando releases…" />;
  }

  if (!data) {
    return <p className="text-sm text-muted-foreground">No se pudo cargar metadata de release.</p>;
  }

  const { build, pointers, history } = data;

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Deploy actual</CardTitle>
          <CardDescription>
            Snapshot del build en ejecución (commit/branch/built). Los campos «Prod homologado» /
            «Último RC» e historial salen del registry humano (
            <code className="text-xs">structura-release-registry.ts</code>
            ), no del deploy automático — actualizado: {data.registryUpdatedAt}.
          </CardDescription>
        </CardHeader>
        <CardContent className="grid gap-3 sm:grid-cols-2">
          <Meta label="Producto" value={build.product === "fm" ? "Structura Market" : "Booking Hub"} />
          <Meta label="Entorno">
            <ReleaseEnvironmentBadge channel={build.channel} />
          </Meta>
          <Meta label="Release" value={build.releaseLabel ?? build.version} />
          <Meta label="Commit" value={build.commit ?? "—"} mono />
          <Meta label="Branch" value={build.branch ?? "—"} mono />
          <Meta label="Built" value={formatBuiltAtUtc(build.builtAt)} />
          <Meta label="Prod homologado" value={pointers.prod ?? "—"} />
          <Meta label="Último RC" value={pointers.latestRc ?? "—"} />
          <Meta label="Rama DEV" value={pointers.devBranch ?? "dev"} />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Historial de releases</CardTitle>
          <CardDescription>
            Trazabilidad manual. Al promover DEV → RC, agregar entrada en{" "}
            <code className="text-xs">structura-release-registry.ts</code> y tag git.
          </CardDescription>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Versión</TableHead>
                <TableHead>Canal</TableHead>
                <TableHead>Rama</TableHead>
                <TableHead>Estado</TableHead>
                <TableHead>Notas</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {history.map((r) => (
                <TableRow key={r.id}>
                  <TableCell className="font-medium tabular-nums">{r.version}</TableCell>
                  <TableCell>
                    <ReleaseEnvironmentBadge channel={r.channel} />
                  </TableCell>
                  <TableCell className="font-mono text-xs">{r.branch}</TableCell>
                  <TableCell>
                    <Badge variant={r.status === "active" ? "default" : "secondary"}>{r.status}</Badge>
                  </TableCell>
                  <TableCell className="max-w-xs text-xs text-muted-foreground">{r.notes ?? "—"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Card className="border-dashed">
        <CardHeader>
          <CardTitle className="text-base">Promover DEV → RC</CardTitle>
          <CardDescription className="text-xs leading-relaxed">
            1) Smoke + QA + gates en <code>STRUCTURA_RELEASE_GATES.md</code>. 2) Branch{" "}
            <code>release/X.Y.Z-rcN</code> desde commit estable en <code>dev</code>. 3) Tag{" "}
            <code>vX.Y.Z-rcN</code>. 4) Entrada en registry + release notes. 5) Homologar. 6) Merge a{" "}
            <code>main</code> solo tras aprobación — nunca hotfix directo sobre RC congelado.
          </CardDescription>
        </CardHeader>
      </Card>
    </div>
  );
}

function Meta({
  label,
  value,
  mono,
  children,
}: {
  label: string;
  value?: string;
  mono?: boolean;
  children?: ReactNode;
}) {
  return (
    <div>
      <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">{label}</p>
      {children ?? (
        <p className={cn("text-sm", mono && "font-mono text-xs")}>{value}</p>
      )}
    </div>
  );
}
